import type { Posting } from "@/lib/api/catalogue";
import { COUNTRY_NAMES } from "./countryNames";

/**
 * Where a posting is. Its page and its `JobPosting` data (src/static/jobPosting.ts) both read
 * the cities here, so the data can name no place the page leaves out. Pure.
 */

/** What continues a word. */
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

/** The cities that name a place, once each, in the board's order. */
export function namedCities(cities: readonly string[]): string[] {
    return [...new Set(cities.filter((city) => city.trim() !== ""))];
}

/** A country's English name from its ISO code, in either case. Null for anything else. */
export function knownCountryName(code: string): string | null {
    const upper = code.toUpperCase();
    return /^[a-z]{2}$/i.test(code) && Object.hasOwn(COUNTRY_NAMES, upper)
        ? (COUNTRY_NAMES[upper] ?? null)
        : null;
}

/** Whether `text` has `words` whole: "Romania" does not say "Oman". */
function hasWhole(text: string, words: string): boolean {
    let found = text.indexOf(words);
    while (found !== -1) {
        const before = text[found - 1] ?? "";
        const after = text[found + words.length] ?? "";
        if (!WORD_CHARACTER.test(before) && !WORD_CHARACTER.test(after)) return true;
        found = text.indexOf(words, found + 1);
    }
    return false;
}

/** Whether `text` has `words` whole, in any case. */
function says(text: string, words: string): boolean {
    return hasWhole(text.toLowerCase(), words.trim().toLowerCase());
}

/**
 * Whether `text` has the country's ISO code whole, in capitals alone, so "us" in a sentence
 * does not count. `JobPosting` data names a country by its code, so the code on the page is
 * enough for it too.
 */
function saysCode(text: string, code: string): boolean {
    return hasWhole(text, code.toUpperCase());
}

/**
 * Where the posting is, as its Location fact says it: the board's own words when they name
 * every city, else the cities, then the country unless its name or its ISO code is already
 * said. Null when the posting says nowhere.
 */
export function placeLine(
    posting: Pick<Posting, "location" | "cities" | "country">,
): string | null {
    const words = posting.location?.trim() ?? "";
    const cities = namedCities(posting.cities);
    const place = words && cities.every((city) => says(words, city)) ? words : cities.join("; ");
    const code = posting.country;
    const country = code ? knownCountryName(code) : null;
    const said = !code || !country || says(place, country) || saysCode(place, code);
    return (said ? [place] : [place, country]).filter(Boolean).join(", ") || null;
}
